/**
 * Rule Detail Store
 *
 * CDSE store for the rule detail/edit view.
 * Policies: ruleDetailQueryPolicy (data fetch), saveRulePolicy (save + navigate).
 */

import { useCallback } from 'react';
import { createStore } from '@commandDrivenStateEngine/core/createStore';
import { Policy } from '@commandDrivenStateEngine/core/policies';
import type { CommandMetaInput, Dispatch } from '@commandDrivenStateEngine/core/types';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import useAuth from '@contexts/useAuth/useAuth';
import { initialState, RuleDetailState } from './state';
import { handlers } from './handlers';
import { invariants } from './invariants';
import { RuleDetailCommand } from './commands';
import { createRuleDetailQueryPolicy } from './policies/ruleDetailQueryPolicy';
import { createSaveRulePolicy } from './policies/saveRulePolicy';

const store = createStore({
  name: 'automation-ruleDetail',
  initialState,
  handlers,
  invariants
});

export const {
  useSelector: useRuleDetailSelector,
  useErrors: useRuleDetailErrors,
  usePolicies: useRuleDetailPolicies,
  getState: getRuleDetailState,
  dispatch: dispatchRuleDetailCommand
} = store;

/**
 * Dispatch hook that attaches the authenticated user to user-sourced commands
 * so the `canApply` capability gates can authorize them. The engine core stays
 * auth-agnostic; this web-app boundary supplies the actor. An explicit
 * `meta.user` (e.g. from tests) is left untouched.
 */
export const useRuleDetailDispatch = (): Dispatch<RuleDetailCommand> => {
  const auth = useAuth();
  const user = auth?.user ?? null;
  return useCallback<Dispatch<RuleDetailCommand>>(
    ( command, metaInput: CommandMetaInput ) =>
      store.dispatch(
        command,
        metaInput.source === 'user' && metaInput.user === undefined
          ? { ...metaInput, user }
          : metaInput
      ),
    [user]
  );
};

/**
 * Policy factory - creates all policies for the ruleDetail store.
 * Accepts backend, ruleId (from URL params), and navigate function.
 */
export const createRuleDetailPolicies = (
  backend: ClientLibraryType,
  ruleId: string,
  navigate: ( path: string ) => void
): Policy<RuleDetailState, RuleDetailCommand>[] => [
  createRuleDetailQueryPolicy( backend, ruleId ),
  createSaveRulePolicy( backend, navigate )
];
